import React, { FC, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AuthenticationContainer } from '@components/organisms/AuthenticationContainer';
import { LoginCard } from '@components/molecules/authentication/LoginCard';
import { Form } from '@components/atoms/Form';
import { Button, ButtonMode } from '@components/atoms/Button';
import { useNotifications } from '@hooks/useNotifications';
import { useResendVerificationEmail } from '@services/useResendVerificationEmail';

export const Login: FC = () => {
    const location = useLocation();
    const { showDefaultNotification } = useNotifications();
    const { t } = useTranslation();

    const translations = {
        emailSuccessfullyVerified: t('login-component.email-successfully-verified'),
        errorEmailAlreadyVerified: t('login-component.error-email-already-verified'),
        errorEmailVerification: t('login-component.error-email-verification'),
        verificationEmailSent: t('login-component.verification-email-sent'),
    };

    const [showEmailVerificationFeedback, setShowEmailVerificationFeedback] = useState<boolean>(false);

    const queryParams = new URLSearchParams(location.search);
    const verificationEmailSent = queryParams.has('verificationEmailSent');
    const emailSuccessfullyVerified = queryParams.has('emailSuccessfullyVerified');
    const errorEmailAlreadyVerified = queryParams.has('errorEmailAlreadyVerified');
    const errorEmailVerification = queryParams.has('errorEmailVerification');

    useEffect(() => {
        if (
            (verificationEmailSent || emailSuccessfullyVerified || errorEmailAlreadyVerified || errorEmailVerification) &&
            !showEmailVerificationFeedback
        ) {
            setShowEmailVerificationFeedback(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (showEmailVerificationFeedback) {
            // dispatch notification
            verificationEmailSent && showDefaultNotification({ type: 'success', message: translations.verificationEmailSent });

            emailSuccessfullyVerified &&
                showDefaultNotification({ type: 'success', message: translations.emailSuccessfullyVerified });

            errorEmailAlreadyVerified &&
                showDefaultNotification({
                    type: 'error',
                    message: translations.errorEmailAlreadyVerified,
                    autoDismiss: undefined,
                    children: <></>,
                });

            errorEmailVerification &&
                showDefaultNotification({
                    type: 'error',
                    message: translations.errorEmailVerification,
                    autoDismiss: undefined,
                    children: <ResendVerificationEmailForm />,
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showEmailVerificationFeedback]);

    return (
        <AuthenticationContainer>
            <LoginCard />
        </AuthenticationContainer>
    );
};

const ResendVerificationEmailForm: FC = () => {
    const location = useLocation();
    const { showDefaultNotification, clearNotifications } = useNotifications();
    const { t } = useTranslation();
    const { request: requestResendVerificationEmail, error, loading, lastResponseTime } = useResendVerificationEmail();

    const translations = {
        resendVerificationEmail: t('login-component.resend-verification-email'),
        verificationEmailSent: t('login-component.verification-email-sent'),
    };

    const queryParams = new URLSearchParams(location.search);
    const uuid = queryParams.get('uuid');

    const formRef = useRef<HTMLFormElement>(null);

    const handleOnClickLogout = () => {
        formRef?.current?.requestSubmit();
    };

    const submitForm = () => {
        requestResendVerificationEmail({ uuid: `${uuid || ''}` });
    };

    // if email sent successfully, show success notification
    useEffect(() => {
        if (lastResponseTime && error === null) {
            clearNotifications();
            showDefaultNotification({ type: 'success', message: translations.verificationEmailSent });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    return (
        <Form formRef={formRef} onSubmit={submitForm}>
            <Button
                classes="mt-4"
                label={translations.resendVerificationEmail}
                mode={ButtonMode.primary}
                onClick={handleOnClickLogout}
                disabled={loading}
            />
        </Form>
    );
};
